import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { Reclamacion, Socio, Carrera, Liga } from '../../models/types';
import { FaSave, FaArrowLeft, FaExclamationTriangle, FaGavel } from 'react-icons/fa';
import { theme } from '../../theme';
import { reclamacionServiceFirestore } from '../../services/ReclamacionServiceFirestore';
import ImageUploader from '../../components/ImageUploader';

// Estilos
const Container = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  border-bottom: 2px solid ${theme.colors.primary.main};
  padding-bottom: 15px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary.main};
  font-size: 1.5rem;
  cursor: pointer;
  margin-right: 15px;
  
  &:hover {
    color: ${theme.colors.secondary.main};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 2.2rem;
  font-weight: ${theme.typography.fontWeights.bold};
  
  svg {
    color: ${theme.colors.primary.main};
  }
`;

const Form = styled.form`
  background-color: white;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 10px;
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  font-size: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
`;

const Button = styled.button`
  background-color: ${theme.colors.primary.main};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  font-size: 1rem;
  
  svg {
    margin-right: 8px;
  }
  
  &:hover {
    background-color: ${theme.colors.secondary.main};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const CancelButton = styled(Button)`
  background-color: ${theme.colors.text.secondary};
  
  &:hover {
    background-color: ${theme.colors.text.primary};
  }
`;

const EvidenceContainer = styled.div`
  margin-top: 15px;
`;

const EvidenceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background-color: ${theme.colors.background.default};
  border-radius: 6px;
  margin-bottom: 12px;
  transition: all 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  }
`;

const EvidenceActions = styled.div`
  display: flex;
  gap: 10px;
`;



const AlertBox = styled.div`
  padding: 16px 20px;
  background-color: rgba(228, 49, 46, 0.05);
  border-left: 4px solid ${theme.colors.secondary.main};
  color: ${theme.colors.text.primary};
  border-radius: 6px;
  margin-bottom: 25px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  line-height: 1.6;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 12px;
    color: ${theme.colors.secondary.main};
    font-size: 1.2rem;
  }
`;

const SuccessBox = styled(AlertBox)`
  background-color: rgba(76, 175, 80, 0.05);
  border-left: 4px solid ${theme.colors.success.main};
  
  svg {
    color: ${theme.colors.success.main};
  }
`;

const InfoMessage = styled.div`
  background-color: rgba(16, 19, 37, 0.03);
  border-left: 4px solid ${theme.colors.primary.main};
  color: ${theme.colors.text.primary};
  padding: 16px 20px;
  margin-bottom: 25px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const HelperText = styled.span`
  display: block;
  margin-top: 5px;
  font-size: 0.8rem;
  color: ${theme.colors.text.secondary};
  font-style: italic;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 10px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 10px 15px;
  border-radius: 6px;
  background-color: ${theme.colors.background.default};
  transition: all 0.2s ease;
  border: 1px solid #ddd;
  
  &:hover {
    background-color: ${theme.colors.background.paper};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  }
  
  input {
    margin-right: 8px;
  }
`;

// Componente principal
const ReclamacionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, loadData } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isComisario, setIsComisario] = useState(true); // En una implementación real, esto vendría de la autenticación
  
  // Estado del formulario
  const [formData, setFormData] = useState<{
    ligaId: string;
    carreraId: string;
    reclamanteSocioId: string;
    reclamadoSocioId: string;
    motivo: string;
    descripcion: string;
    evidencias: string[];
    vuelta?: number;
    minutoIncidente?: number;
    segundoIncidente?: number;
  }>({
    ligaId: '',
    carreraId: '',
    reclamanteSocioId: '',
    reclamadoSocioId: '',
    motivo: '',
    descripcion: '',
    evidencias: [],
    vuelta: undefined,
    minutoIncidente: undefined,
    segundoIncidente: undefined
  });
  
  // Cargar datos de la reclamación si estamos editando
  useEffect(() => {
    const cargarReclamacion = async () => {
      if (id && id !== 'new') {
        try {
          setLoading(true);
          const reclamacion = data.reclamaciones.find((r: Reclamacion) => r.id === id);
          
          if (reclamacion) {
            setFormData({
              ligaId: reclamacion.ligaId,
              carreraId: reclamacion.carreraId,
              reclamanteSocioId: reclamacion.reclamanteSocioId,
              reclamadoSocioId: reclamacion.reclamadoSocioId,
              motivo: reclamacion.motivo,
              descripcion: reclamacion.descripcion,
              evidencias: reclamacion.evidencias || [],
              vuelta: reclamacion.vuelta,
              minutoIncidente: reclamacion.minutoIncidente,
              segundoIncidente: reclamacion.segundoIncidente
            });
          } else {
            setError('No se encontró la reclamación solicitada');
          }
        } catch (err) {
          console.error('Error al cargar la reclamación:', err);
          setError('Error al cargar los datos de la reclamación');
        } finally {
          setLoading(false);
        }
      }
    };
    
    cargarReclamacion();
  }, [id, data.reclamaciones]);
  
  // Manejar cambios en el formulario
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? undefined : parseInt(value, 10)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  

  
  // Manejar subida de evidencias
  const handleEvidenceUpload = (imageUrl: string) => {
    setFormData(prev => ({
      ...prev,
      evidencias: [...prev.evidencias, imageUrl]
    }));
  };
  
  // Eliminar evidencia
  const handleRemoveEvidence = (index: number) => {
    setFormData(prev => ({
      ...prev,
      evidencias: prev.evidencias.filter((_, i) => i !== index)
    }));
  };
  
  // Obtener carreras por liga
  const getCarrerasPorLiga = (ligaId: string) => {
    return data.carreras
    .filter((c: Carrera) => c.ligaId === ligaId)
    .sort((a: Carrera, b: Carrera) => a.nombre.localeCompare(b.nombre));
  };
  
  // Enviar formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    // Validar campos obligatorios
    if (!formData.ligaId) {
      setError('Debes seleccionar una liga');
      return;
    }
    
    if (!formData.carreraId) {
      setError('Debes seleccionar una carrera');
      return;
    }
    
    if (!formData.reclamanteSocioId) {
      setError('Debes seleccionar el piloto que realiza la reclamación');
      return;
    }
    
    if (!formData.reclamadoSocioId) {
      setError('Debes seleccionar el piloto reclamado');
      return;
    }
    
    if (!formData.motivo) {
      setError('Debes proporcionar un motivo para la reclamación');
      return;
    }
    
    if (!formData.descripcion) {
      setError('Debes proporcionar una descripción detallada del incidente');
      return;
    }
    
    try {
      setLoading(true);
      
      if (!id || id === 'new') {
        // Crear nueva reclamación
        const nuevaReclamacion: Omit<Reclamacion, 'id'> = {
          ligaId: formData.ligaId,
          carreraId: formData.carreraId,
          reclamanteSocioId: formData.reclamanteSocioId,
          reclamadoSocioId: formData.reclamadoSocioId,
          motivo: formData.motivo,
          descripcion: formData.descripcion,
          evidencias: formData.evidencias,
          fechaReclamacion: new Date().toISOString().split('T')[0],
          estado: 'pendiente',
          vuelta: formData.vuelta,
          minutoIncidente: formData.minutoIncidente,
          segundoIncidente: formData.segundoIncidente
        };
        
        const resultado = await reclamacionServiceFirestore.create(nuevaReclamacion);
        
        if (resultado) {
          setSuccess('Reclamación enviada correctamente');
          setTimeout(() => {
            loadData();
            navigate('/reclamaciones');
          }, 2000);
        } else {
          setError('Error al enviar la reclamación');
        }
      } else {
        // Actualizar reclamación existente
        const reclamacionActualizada: Partial<Reclamacion> = {
          ligaId: formData.ligaId,
          carreraId: formData.carreraId,
          reclamanteSocioId: formData.reclamanteSocioId,
          reclamadoSocioId: formData.reclamadoSocioId,
          motivo: formData.motivo,
          descripcion: formData.descripcion,
          evidencias: formData.evidencias,
          vuelta: formData.vuelta,
          minutoIncidente: formData.minutoIncidente,
          segundoIncidente: formData.segundoIncidente
        };
        
        // Asegurarse de que id no sea undefined antes de usarlo
        if (id) {
          const resultado = await reclamacionServiceFirestore.update(id, reclamacionActualizada);
          
          if (resultado) {
            setSuccess('Reclamación actualizada correctamente');
            setTimeout(() => {
              loadData();
              navigate('/reclamaciones');
            }, 2000);
          } else {
            setError('Error al actualizar la reclamación');
          }
        } else {
          setError('ID de reclamación no válido');
        }
      }
    } catch (err) {
      console.error('Error al procesar la reclamación:', err);
      setError('Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/reclamaciones')}>
          <FaArrowLeft />
        </BackButton>
        <Title><FaGavel /> {!id || id === 'new' ? 'Nueva Reclamación' : 'Editar Reclamación'}</Title>
      </Header>
      
      {(!id || id === 'new') && (
        <InfoMessage>
          Completa el siguiente formulario para presentar una reclamación sobre un incidente ocurrido durante una carrera. Proporciona toda la información posible y adjunta evidencias que respalden tu reclamación.
        </InfoMessage>
      )}
      
      <Form onSubmit={handleSubmit} style={id && id !== 'new' ? { pointerEvents: 'none', opacity: 0.8 } : {}}>
        {error && (
          <AlertBox>
            <FaExclamationTriangle />
            {error}
          </AlertBox>
        )}
        
        {success && (
          <SuccessBox>
            <FaExclamationTriangle />
            {success}
          </SuccessBox>
        )}
        
        <FormGroup>
          <Label htmlFor="ligaId">Liga</Label>
          <Select 
            id="ligaId"
            name="ligaId"
            value={formData.ligaId}
            onChange={(e) => {
              handleChange(e);
              setFormData(prev => ({ ...prev, carreraId: '' })); // Resetear carrera al cambiar liga
            }}
            required
            disabled={loading}
          >
            <option value="">Selecciona una liga</option>
            {data.ligas.map((liga: Liga) => (
              <option key={liga.id} value={liga.id}>
                {liga.nombre}
              </option>
            ))}
          </Select>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="carreraId">Carrera</Label>
          <Select 
            id="carreraId"
            name="carreraId"
            value={formData.carreraId}
            onChange={handleChange}
            required
            disabled={loading || !formData.ligaId}
          >
            <option value="">Selecciona una carrera</option>
            {getCarrerasPorLiga(formData.ligaId).map((carrera: Carrera) => (
              <option key={carrera.id} value={carrera.id}>
                {carrera.nombre} - {carrera.circuito} - {carrera.fecha}
              </option>
            ))}
          </Select>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="reclamanteSocioId">Piloto Reclamante</Label>
          <Select 
            id="reclamanteSocioId"
            name="reclamanteSocioId"
            value={formData.reclamanteSocioId}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Selecciona el piloto que realiza la reclamación</option>
            {data.socios.map((socio: Socio) => (
              <option key={socio.id} value={socio.id}>
                {socio.display_name || socio.user_login || socio.NOMBRE_SERVERS_VRS || (socio.nombre && socio.apellido ? `${socio.nombre} ${socio.apellido}` : 'Piloto sin nombre')}
              </option>
            ))}
          </Select>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="reclamadoSocioId">Piloto Reclamado</Label>
          <Select 
            id="reclamadoSocioId"
            name="reclamadoSocioId"
            value={formData.reclamadoSocioId}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Selecciona el piloto reclamado</option>
            {data.socios
              .filter((s: Socio) => s.id !== formData.reclamanteSocioId) // Excluir al reclamante
              .map((socio: Socio) => (
                <option key={socio.id} value={socio.id}>
                  {socio.display_name || socio.user_login || socio.NOMBRE_SERVERS_VRS || (socio.nombre && socio.apellido ? `${socio.nombre} ${socio.apellido}` : 'Piloto sin nombre')}
                </option>
              ))
            }
          </Select>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="vuelta">Vuelta del Incidente</Label>
          <Input 
            type="number" 
            id="vuelta"
            name="vuelta"
            value={formData.vuelta || ''}
            onChange={handleChange}
            placeholder="Número de vuelta en la que ocurrió el incidente"
            min="1"
            disabled={loading}
          />
        </FormGroup>
        
        <FormGroup>
          <Label>Momento exacto del incidente</Label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <Label htmlFor="minutoIncidente">Minuto</Label>
              <Input 
                type="number" 
                id="minutoIncidente"
                name="minutoIncidente"
                value={formData.minutoIncidente ?? ''}
                onChange={handleChange}
                placeholder="Minuto"
                min="0"
                max="59"
                disabled={loading}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Label htmlFor="segundoIncidente">Segundo</Label>
              <Input 
                type="number" 
                id="segundoIncidente"
                name="segundoIncidente"
                value={formData.segundoIncidente ?? ''}
                onChange={handleChange}
                placeholder="Segundo"
                min="0"
                max="59"
                step="0.001"
                disabled={loading}
              />
            </div>
          </div>
          <HelperText>Especifica el minuto y segundo exacto del replay oficial</HelperText>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="motivo">Motivo de la Reclamación</Label>
          <Input 
            type="text" 
            id="motivo"
            name="motivo"
            value={formData.motivo}
            onChange={handleChange}
            placeholder="Resumen breve del motivo de la reclamación"
            required
            disabled={loading}
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="descripcion">Descripción Detallada</Label>
          <TextArea 
            id="descripcion"
            name="descripcion"
            value={formData.descripcion}
            onChange={handleChange}
            placeholder="Describe detalladamente lo que ocurrió, incluyendo la vuelta, curva, y cualquier otra información relevante..."
            required
            disabled={loading}
          />
        </FormGroup>
        
        <FormGroup>
          <Label>Evidencias</Label>
          <p>Puedes subir capturas de pantalla, vídeos u otros documentos que respalden tu reclamación.</p>
          
          <EvidenceContainer>
            {formData.evidencias.map((evidencia, index) => (
              <EvidenceItem key={index}>
                <div>Evidencia {index + 1}</div>
                <EvidenceActions>
                  <a href={evidencia} target="_blank" rel="noopener noreferrer">
                    Ver
                  </a>
                  <button 
                    type="button" 
                    onClick={() => handleRemoveEvidence(index)}
                    style={{ color: 'red', border: 'none', background: 'none', cursor: 'pointer' }}
                  >
                    Eliminar
                  </button>
                </EvidenceActions>
              </EvidenceItem>
            ))}
            <ImageUploader
              currentImageUrl=""
              onImageChange={handleEvidenceUpload}
              folder="evidencias"
              placeholder="Haz clic aqui para subir otra evidencia"
            />
          </EvidenceContainer>
        </FormGroup>
        
        <ButtonContainer>
          <CancelButton type="button" onClick={() => navigate('/reclamaciones')}>
            Volver
          </CancelButton>
          {(!id || id === 'new') && (
            <Button type="submit" disabled={loading}>
              <FaSave /> {loading ? 'Enviando...' : 'Enviar Reclamación'}
            </Button>
          )}
        </ButtonContainer>
      </Form>
    </Container>
  );
};

export default ReclamacionDetail;
